import React, { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import platino from "../../../images/platino.png";
import oro from "../../../images/oro.png";
import argento from "../../../images/argento.png";
import bronzo from "../../../images/bronzo.png";
import TabellaLista from "../Tabella/TabellaLista";
import { ColumnFilter } from "../table/FilteringTable/ColumnFilter";
import MyApiClient from "../../../api-client/MyApiClient";
import { TROFEI, formatoData, skillPerSport, traguardiInCorso, trofeiPerSport } from "./certificazioniSportive";

/*
 * Tab «Traguardi» della scheda del player e del junior (segnalazione del 04/11/2026):
 * - Trofei Move della stagione (settembre-agosto), divisi per sport, e i traguardi del periodo in corso con
 *   l'avanzamento verso il trofeo. Prima c'erano solo i totali, e «Ultimo aggiornamento» era l'ora della pagina.
 * - Livello SportId: i punti di sempre (non ripartono a settembre come i trofei).
 * - Traguardi skill: le skill certificate da un maestro, una tabella per sport, con data e maestro.
 * Tolto «I miei ultimi traguardi», che restava sempre vuoto.
 */

const IMMAGINI = { PLATINUM: platino, GOLD: oro, SILVER: argento, BRONZE: bronzo };
const CHIAVI_TOTALI = { PLATINUM: 'platinum', GOLD: 'gold', SILVER: 'silver', BRONZE: 'bronze' };

const colonneTrofei = [
    { Header: 'Sport', accessor: 'sport', Filter: ColumnFilter },
    ...TROFEI.map(({ tipo, nome }) => ({ Header: nome, accessor: tipo, Filter: ColumnFilter })),
];
const colonneInCorso = [
    { Header: 'Sport', accessor: 'sport', Filter: ColumnFilter },
    { Header: 'Traguardo', accessor: 'goal', Filter: ColumnFilter },
    { Header: 'Periodo', accessor: 'period', Filter: ColumnFilter },
    { Header: 'Trofeo', accessor: 'trophy', Filter: ColumnFilter },
    { Header: 'Avanzamento', accessor: 'progress', Filter: ColumnFilter, Cell: ({ value }) => `${value}%` },
];
const colonneSkill = [
    { Header: 'Skill', accessor: 'skill', Filter: ColumnFilter },
    { Header: 'Valore', accessor: 'value', Filter: ColumnFilter },
    { Header: 'Certificata il', accessor: 'certifiedAt', Filter: ColumnFilter, compact: true },
    { Header: 'Maestro', accessor: 'coach', Filter: ColumnFilter },
];

export const TraguardiSportivi = ({ userId }) => {
    const [dati, setDati] = useState(null);

    useEffect(() => {
        if (!userId) return undefined;
        let attivo = true;
        const leggi = async () => {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            const api = new MyApiClient();
            const [stagione, skill] = await Promise.all([api.getSeasonCurrentRewards(token, userId), api.getSportsSkills(token, userId)]);
            const season = stagione?.error ? null : stagione?.season;
            const [vinti, incorso] = season
                ? await Promise.all([api.getMoveGoalsRewards(token, userId, season), api.getLiveMoveGoals(token, userId, season)])
                : [[], []];
            if (!attivo) return;
            setDati({
                stagione: season,
                totali: stagione?.rewards?.trophies || {},
                punti: stagione?.rewards?.points ?? 0,
                perSport: trofeiPerSport(vinti?.error ? [] : vinti),
                inCorso: traguardiInCorso(incorso?.error ? [] : incorso),
                skill: skillPerSport(skill?.error ? [] : skill),
            });
        };
        leggi();
        return () => { attivo = false; };
    }, [userId]);

    if (!dati) return null;

    return (
        <>
            <Row>
                <Card className="cardAuto col me-4">
                    <Card.Header>
                        <h4 className="card-title">Trofei Move{dati.stagione ? ` · stagione ${dati.stagione}` : ''}</h4>
                    </Card.Header>
                    <Card.Body>
                        <Row className="mt-3">
                            {TROFEI.map(({ tipo, nome }) => (
                                <Col key={tipo} className="col-xl-3 text-center">
                                    <img src={IMMAGINI[tipo]} style={{ width: '60px', maxWidth: '100%' }} alt="" />
                                    <h4>{dati.totali[CHIAVI_TOTALI[tipo]] ?? 0}</h4>
                                    <div>{nome}</div>
                                </Col>
                            ))}
                        </Row>
                        <p className="sid-nota-leggibile mt-3 mb-0">
                            {dati.perSport.ultimo ? `Ultimo trofeo vinto il ${formatoData(dati.perSport.ultimo)}.` : 'Nessun trofeo in questa stagione.'}
                            {' '}I trofei ripartono da zero a settembre e arrivano quando il player sincronizza le attività dall'app.
                        </p>
                    </Card.Body>
                </Card>
                <Card className="cardAuto col">
                    <Card.Header><h4 className="card-title">Livello SportId</h4></Card.Header>
                    <Card.Body>
                        <h3 className="text-primary livelloMove">{dati.punti}</h3>
                        <p className="sid-nota-leggibile mb-0">Punti da quando usa SportID: non ripartono a settembre.</p>
                    </Card.Body>
                </Card>
            </Row>
            <Row>
                <TabellaLista
                    cardTitle={'Trofei Move per sport'}
                    hasSearch={false}
                    hasFilter={false}
                    hasAddMore={false}
                    data_column={colonneTrofei}
                    data_json={dati.perSport.righe}
                />
            </Row>
            <Row>
                <TabellaLista
                    cardTitle={'Traguardi Move in corso'}
                    hasSearch={false}
                    hasFilter={false}
                    hasAddMore={false}
                    data_column={colonneInCorso}
                    data_json={dati.inCorso}
                />
            </Row>
            {dati.skill.length === 0 && (
                <Row>
                    <Card className="cardAuto">
                        <Card.Header><h4 className="card-title">Traguardi skill</h4></Card.Header>
                        <Card.Body><div className="mt-4 mb-4 text-center">Nessuna skill certificata da un maestro</div></Card.Body>
                    </Card>
                </Row>
            )}
            {dati.skill.map((sport) => (
                <Row key={sport.sportType}>
                    <TabellaLista
                        cardTitle={`Traguardi skill · ${sport.sport}`}
                        hasSearch={false}
                        hasFilter={false}
                        hasAddMore={false}
                        data_column={colonneSkill}
                        data_json={sport.righe}
                    />
                </Row>
            ))}
        </>
    );
};

export default TraguardiSportivi;
